import { useToaster } from '@innovationdepartment/proxima-ui';
import {
  type ColumnDef,
  getCoreRowModel,
  getFilteredRowModel,
  getPaginationRowModel,
  getSortedRowModel,
  type SortingState,
  useReactTable
} from '@tanstack/react-table';
import useBrandTagMappingApi from 'hooks/useBrandTagMappingsApi';
import { debounce } from 'lodash';
import { useEffect, useMemo, useState } from 'react';
import type { BrandTag } from 'types/brandTag';
import BrandsUsingTagView from './BrandsUsingTag.View';
import RemoveTagFromBrandConfirmation from './RemoveTagFromBrandConfirmation.Container';

type BrandInfo = {
  brandId: string;
  name: string;
  domain: string;
};

type BrandsUsingTagProps = {
  tag: BrandTag;
  onBrandCountChange?: (count: number) => void;
};

const BrandsUsingTagContainer = ({ tag, onBrandCountChange }: BrandsUsingTagProps) => {
  const [brands, setBrands] = useState<BrandInfo[]>([]);
  const [loading, setLoading] = useState(true);
  const [globalFilter, setGlobalFilter] = useState('');
  const [isConfirmationOpen, setIsConfirmationOpen] = useState(false);
  const [brandToRemove, setBrandToRemove] = useState<{ brandId: string; name: string } | null>(
    null
  );
  const [isRemoving, setIsRemoving] = useState(false);
  const [sorting, setSorting] = useState<SortingState>([]);
  const { fetchBrandsByTagId, removeTagFromBrand } = useBrandTagMappingApi();
  const { showToaster } = useToaster();

  const fetchBrandsUsingTag = async () => {
    setLoading(true);
    const response = await fetchBrandsByTagId(tag.id);
    setBrands(response);
    onBrandCountChange?.(response.length);
    setLoading(false);
  };

  const handleRemoveBrand = (brandId: string) => {
    const brand = brands.find(b => b.brandId === brandId);
    if (brand) {
      setBrandToRemove({ brandId: brand.brandId, name: brand.name });
      setIsConfirmationOpen(true);
    }
  };

  const confirmRemoveBrand = async () => {
    if (!brandToRemove) return;
    setIsRemoving(true);
    const response = await removeTagFromBrand(brandToRemove.brandId, tag.id);
    if (response.error) {
      setIsRemoving(false);
      return;
    }
    await fetchBrandsUsingTag();
    showToaster({ message: 'Tag removed from brand.', variant: 'success' });
    setIsConfirmationOpen(false);
    setBrandToRemove(null);
    setIsRemoving(false);
  };

  const closeConfirmation = () => {
    setIsConfirmationOpen(false);
    setBrandToRemove(null);
  };

  const debouncedSearch = debounce((value: string) => {
    setGlobalFilter(value);
  }, 300);

  const handleSearch = (value: string) => {
    debouncedSearch(value);
  };

  // biome-ignore lint/correctness/useExhaustiveDependencies: pre-Biome migration; the app is already working as expected with these dependency arrays
  useEffect(() => {
    if (tag.id) {
      fetchBrandsUsingTag();
    }
  }, [tag.id]);

  const columns = useMemo<ColumnDef<BrandInfo>[]>(
    () => [
      {
        accessorKey: 'name',
        header: 'BRAND NAME',
        cell: info => info.getValue(),
        enableSorting: true
      },
      {
        accessorKey: 'brandId',
        header: 'BRAND ID',
        cell: info => info.getValue(),
        enableSorting: true
      },
      {
        id: 'domain',
        accessorKey: 'domain',
        header: 'BRAND URL',
        cell: info => {
          const domain = info.getValue() as string;
          return {
            domain,
            url: `https://${domain}`
          };
        },
        enableSorting: true
      },
      {
        id: 'actions',
        header: 'ACTIONS',
        cell: info => info.row.original.brandId,
        enableSorting: false
      }
    ],
    []
  );

  // Create table instance
  const table = useReactTable({
    data: brands,
    columns,
    state: {
      sorting,
      globalFilter
    },
    onSortingChange: setSorting,
    onGlobalFilterChange: setGlobalFilter,
    getCoreRowModel: getCoreRowModel(),
    getSortedRowModel: getSortedRowModel(),
    getFilteredRowModel: getFilteredRowModel(),
    getPaginationRowModel: getPaginationRowModel(),
    initialState: {
      pagination: {
        pageSize: 10
      }
    },
    globalFilterFn: (row, _columnId, filterValue) => {
      const brand = row.original;
      const search = String(filterValue).toLowerCase();
      return (
        brand.name.toLowerCase().includes(search) ||
        brand.brandId.toLowerCase().includes(search) ||
        brand.domain.toLowerCase().includes(search)
      );
    }
  });

  return (
    <>
      <BrandsUsingTagView
        table={table}
        loading={loading}
        onSearch={handleSearch}
        onRemoveBrand={handleRemoveBrand}
      />
      {!!brandToRemove && (
        <RemoveTagFromBrandConfirmation
          open={isConfirmationOpen}
          close={closeConfirmation}
          confirm={confirmRemoveBrand}
          brandName={brandToRemove.name}
          tagOriginalName={tag.originalName}
          isLoading={isRemoving}
        />
      )}
    </>
  );
};

export default BrandsUsingTagContainer;
